import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, Container, Typography, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, Button, Chip, IconButton, Modal, TextField, 
  Select, MenuItem, FormControl, InputLabel, Stack, InputAdornment, Tooltip,
  Tabs, Tab, Switch, FormControlLabel, Alert, Card, Avatar, Grid
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { collection, getDocs, doc, updateDoc, addDoc, deleteDoc, serverTimestamp, orderBy, query } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import Navbar from './Navbar';

const AdminPortal = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0); // 0: Users, 1: Reports, 2: Sponsored
  
  // Data States
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [sponsoredPosts, setSponsoredPosts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal/Edit States
  const [open, setOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Sponsored Post Form States
  const [caption, setCaption] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [isPublishing, setIsPublishing] = useState(false);

  // User Form States
  const [role, setRole] = useState('user');
  const [status, setStatus] = useState('trial');
  const [isActive, setIsActive] = useState(true);
  const [expiryDate, setExpiryDate] = useState('');

  useEffect(() => {
    fetchData();
  }, [tabValue]);

  const fetchData = async () => {
    const userSnap = await getDocs(collection(db, "users"));
    setUsers(userSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    
    const reportSnap = await getDocs(collection(db, "reports"));
    setReports(reportSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

    const sponsoredQuery = query(collection(db, "sponsoredPosts"), orderBy("timestamp", "desc"));
    const sponsoredSnap = await getDocs(sponsoredQuery);
    setSponsoredPosts(sponsoredSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  // --- Sponsored Post Logic ---
  const handlePublishPost = async () => {
    if (!imageFile || !caption) return alert("Image and caption are required");
    setIsPublishing(true);
    try {
      const storageRef = ref(storage, `sponsored/${Date.now()}_${imageFile.name}`);
      const uploadTask = await uploadBytes(storageRef, imageFile);
      const downloadURL = await getDownloadURL(uploadTask.ref);

      await addDoc(collection(db, 'sponsoredPosts'), {
        caption,
        imageUrl: downloadURL,
        timestamp: serverTimestamp(),
        isActive: true
      });
      
      setCaption('');
      setImageFile(null);
      fetchData();
      alert("Post Published!");
    } catch (e) { alert("Upload failed"); }
    finally { setIsPublishing(false); }
  };

  const handleDeletePost = async (postId) => {
    if(window.confirm("Delete this sponsored post?")) {
      await deleteDoc(doc(db, "sponsoredPosts", postId));
      fetchData();
    }
  };

  // --- User Logic ---
  const handleEditOpen = (user) => {
    setSelectedUser(user);
    setRole(user.role || 'user');
    setStatus(user.status || 'trial');
    setIsActive(user.isActive !== false);
    let dateObj = user.subscription?.expiresAt?.toDate ? user.subscription.expiresAt.toDate() : new Date();
    setExpiryDate(dateObj.toISOString().split('T')[0]);
    setOpen(true);
  };

  const handleUpdateUser = async () => {
    setIsUpdating(true);
    try {
      const userRef = doc(db, "users", selectedUser.id);
      await updateDoc(userRef, {
        role, status, isActive,
        'subscription.expiresAt': new Date(expiryDate),
        'subscription.lastAdminUpdate': new Date()
      });
      setOpen(false);
      fetchData();
    } catch (error) { alert("Update failed"); }
    finally { setIsUpdating(false); }
  };

  const handleResolveReport = async (reportId, statusUpdate) => {
    const reportRef = doc(db, "reports", reportId);
    await updateDoc(reportRef, { status: statusUpdate });
    fetchData();
  };

  return (
    <Box sx={{ py: 12, bgcolor: '#f4f7fb', minHeight: '100vh' }}>
      <Navbar />
      <Container maxWidth="lg">
        <Typography variant="h4" fontWeight={800} mb={4}>Admin Control Center</Typography>

        <Tabs value={tabValue} onChange={(e, val) => setTabValue(val)} sx={{ mb: 4 }}>
          <Tab label="Users" />
          <Tab label="Reports" />
          <Tab label="Sponsored Content" />
        </Tabs>

        {/* USERS TAB */}
        {tabValue === 0 && (
          <>
            <TextField
              fullWidth
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mb: 3, bgcolor: 'white', borderRadius: '12px' }}
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'gray' }} /> }}
            />
            <TableContainer component={Paper} sx={{ borderRadius: '20px' }}>
              <Table>
                <TableHead sx={{ bgcolor: '#B200BB' }}>
                  <TableRow>
                    <TableCell sx={{ color: '#fff' }}>User</TableCell>
                    <TableCell sx={{ color: '#fff' }}>Account</TableCell>
                    <TableCell sx={{ color: '#fff' }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.filter(u => u.email?.includes(searchTerm)).map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700}>{user.displayName}</Typography>
                        <Typography variant="caption">{user.email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={user.status} size="small" sx={{ mr: 1 }} />
                        <Chip label={user.isActive === false ? "Banned" : "Active"} color={user.isActive === false ? "error" : "success"} size="small" />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => handleEditOpen(user)}><EditIcon /></IconButton>
                        <IconButton onClick={() => navigate(`/admin/user/${user.id}`)}><VisibilityIcon /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* REPORTS TAB */}
        {tabValue === 1 && (
          <TableContainer component={Paper} sx={{ borderRadius: '20px' }}>
            <Table>
              <TableHead sx={{ bgcolor: '#d32f2f' }}>
                <TableRow>
                  <TableCell sx={{ color: '#fff' }}>Reporter</TableCell>
                  <TableCell sx={{ color: '#fff' }}>Remarks</TableCell>
                  <TableCell sx={{ color: '#fff' }} align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell><Typography variant="caption">{report.reporterId}</Typography></TableCell>
                    <TableCell>{report.remarks}</TableCell>
                    <TableCell align="right">
                      {report.status === 'pending' ? (
                        <Button variant="contained" color="error" size="small" onClick={() => handleResolveReport(report.id, 'resolved')}>Resolve</Button>
                      ) : <Chip label="Resolved" size="small" />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* SPONSORED TAB */}
        {tabValue === 2 && (
          <Grid container spacing={4}>
            <Grid item xs={12} md={5}>
              <Paper sx={{ p: 3, borderRadius: '20px' }}>
                <Typography variant="h6" fontWeight={700} mb={2}>New Sponsored Post</Typography>
                <Stack spacing={2}>
                  <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} sx={{ py: 3, borderStyle: 'dashed' }}>
                    {imageFile ? imageFile.name : "Upload Post Image"}
                    <input type="file" hidden accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} />
                  </Button>
                  <TextField label="Caption" multiline rows={3} value={caption} onChange={(e) => setCaption(e.target.value)} />
                  <Button 
                    variant="contained" 
                    fullWidth 
                    onClick={handlePublishPost} 
                    disabled={isPublishing}
                    sx={{ bgcolor: '#C00068', borderRadius: '50px' }}
                  >
                    {isPublishing ? "Publishing..." : "Publish to App"}
                  </Button>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={7}>
              <Typography variant="h6" fontWeight={700} mb={2}>Live Sponsored Posts</Typography>
              <Stack spacing={2}>
                {sponsoredPosts.map(post => (
                  <Card key={post.id} sx={{ display: 'flex', p: 2, borderRadius: '15px' }}>
                    <Avatar variant="rounded" src={post.imageUrl} sx={{ width: 80, height: 80, mr: 2 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>{post.caption}</Typography>
                      <Typography variant="caption" color="text.secondary">Published: {post.timestamp?.toDate().toLocaleDateString()}</Typography>
                    </Box>
                    <IconButton color="error" onClick={() => handleDeletePost(post.id)}><DeleteIcon /></IconButton>
                  </Card>
                ))}
              </Stack>
            </Grid>
          </Grid>
        )}
      </Container>

      {/* MODAL (Existing logic) */}
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box sx={modalStyle}>
          <Typography variant="h6" fontWeight={800} mb={3}>Moderate User</Typography>
          <Stack spacing={3}>
            <FormControlLabel
              control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} color="error" />}
              label="Account Active"
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
                <MenuItem value="trial">Trial</MenuItem>
                <MenuItem value="premium">Premium</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Expiry" type="date" fullWidth value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            <Button variant="contained" fullWidth onClick={handleUpdateUser} sx={{ bgcolor: '#B200BB', borderRadius: '50px' }}>Save</Button>
          </Stack>
        </Box>
      </Modal>
    </Box>
  );
};

const modalStyle = { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 400, bgcolor: 'background.paper', borderRadius: '24px', p: 4 };

export default AdminPortal;